/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "PRICES" TAB                           *
 *  File: apps/web/src/pages/bridge/PricesTab.js                   *
 *  What it does: the approved price list, and a history drawer    *
 *  for any one item (every change, old price and new, who, why).  *
 *  Reads: GET /prices, GET /prices/:zohoItemId/history.           *
 *  Writes: nothing.                                               *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE HISTORY DRAWER                                 *
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS } from './bridgeWords';
import { LoadState, ToneChip, formatMoney, formatWhen } from './bridgeParts';
import { fetchPrices, fetchPriceHistory } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE HISTORY DRAWER                                 *
 *****************************************************************/

/* ---- HistoryDrawer({ priceRow, onClose }) -- one item's changes ---- */
function HistoryDrawer({ priceRow, onClose }) {
  const [historyRows, setHistoryRows] = useState([]);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    let isStillOpen = true;

    async function loadHistory() {
      const answer = await fetchPriceHistory(priceRow.zohoItemId);

      if (!isStillOpen) {
        return;
      }

      if (!answer.ok) {
        setLoadState('error');
        return;
      }

      setHistoryRows(answer.data || []);
      setLoadState('ready');
    }

    loadHistory();

    return () => {
      isStillOpen = false;
    };
  }, [priceRow.zohoItemId]);

  return (
    <div className="zb-drawer" role="dialog" aria-modal="false" aria-labelledby="zb-history-title">
      <div className="zb-row zb-row-spread">
        <h3 id="zb-history-title" className="zb-card-title">
          {BRIDGE_WORDS.priceHistory}: {priceRow.name}
        </h3>
        <button type="button" className="zb-button zb-button-plain" onClick={onClose}>
          {BRIDGE_WORDS.closeHistory}
        </button>
      </div>

      {loadState !== 'ready' ? <LoadState state={loadState} /> : null}
      {loadState === 'ready' && historyRows.length === 0 ? <p className="zb-quiet">{BRIDGE_WORDS.historyEmpty}</p> : null}

      <ul className="zb-history">
        {historyRows.map((oneRow) => (
          <li key={oneRow.id}>
            <strong>{formatWhen(oneRow.appliedAt)}</strong>
            <span>
              {oneRow.oldRate === null ? 'first price ' : formatMoney(oneRow.oldRate) + ' → '}
              {formatMoney(oneRow.newRate)}
              {oneRow.oldStatus && oneRow.oldStatus !== oneRow.newStatus
                ? ' (' + oneRow.oldStatus + ' → ' + oneRow.newStatus + ')'
                : ''}
            </span>
            {oneRow.why ? <span className="zb-small">"{oneRow.why}"</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/*****************************************************************
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

export default function PricesTab() {
  const [priceRows, setPriceRows] = useState([]);         // the price list
  const [loadState, setLoadState] = useState('loading');  // loading / ready / error
  const [openPrice, setOpenPrice] = useState(null);       // the item whose history is open

  /* ---- loadPrices() -- read the list ---- */
  const loadPrices = useCallback(async () => {
    setLoadState('loading');

    const answer = await fetchPrices();

    if (!answer.ok) {
      setLoadState('error');
      return;
    }

    setPriceRows(answer.data || []);
    setLoadState('ready');
  }, []);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  return (
    <section aria-labelledby="zb-tab-prices-title">
      <h2 id="zb-tab-prices-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabPrices}</h2>

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={loadPrices} /> : null}
      {loadState === 'ready' && priceRows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.pricesEmpty} /> : null}

      {openPrice ? <HistoryDrawer priceRow={openPrice} onClose={() => setOpenPrice(null)} /> : null}

      <ul className="zb-list">
        {priceRows.map((onePrice) => (
          <li className="zb-list-row" key={onePrice.id}>
            <div className="zb-list-main">
              <strong>{onePrice.name}</strong>
              <span className="zb-money">{formatMoney(onePrice.rate)}</span>
              {onePrice.unit ? <span className="zb-small">per {onePrice.unit}</span> : null}
            </div>
            <div className="zb-row zb-row-tight">
              {onePrice.status === 'inactive'
                ? <ToneChip tone="neutral" icon="cross" word="Inactive in Zoho" />
                : <ToneChip tone="ok" icon="check" word="Active" />}
              <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenPrice(onePrice)}>
                <BridgeIcon name="history" />
                <span>{BRIDGE_WORDS.priceHistory}</span>
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
